/**
 * Utility functions for handling trip activities
 */

import { knownActivityDurationMinutes } from './activityUtilities';
import { walkPlannedMinutes } from './walkLabels';

/**
 * Coerce a DB value (0/1/true/false/null) to a strict boolean or null.
 * SQLite returns booleans as 0/1; some paths return true/false. Anything
 * else (undefined, null, empty string) becomes null — "unset".
 */
export const toBool = (val) =>
  val === 1 || val === true ? true :
  val === 0 || val === false ? false :
  null;

/**
 * Creates a standardized activity object from a collection or saved item
 *
 * ARCHITECTURE NOTE:
 * - Collections API returns: { id: "ChIJ..." } (clean Google Place ID)
 * - Both collections and saved items use the same unified save system (saved_items table)
 * - item.id is ALWAYS a clean ID (ChIJ... for places, tm_... for events, custom_... for custom)
 *
 * @param {Object} item - Collection item or saved item from API
 * @param {string} source - Source type ('collection' or 'saved') - used for tracking only
 * @returns {Object} Standardized activity object
 */
export const createActivityFromItem = (item, source = 'collection') => {
  // item.id is the clean content_id from saved_items table
  // No extraction needed - database stores clean IDs
  const cleanPlaceId = item.id;

  // Generate unique frontend ID for React keys and state management
  // Backend will extract the place_id from activity.placeId field
  const frontendId = `saved_${cleanPlaceId}_${Date.now()}`;

  // Normalize coordinates from different API response formats
  const coordinates = item.coordinates || (item.lat && item.lng ? { lat: item.lat, lng: item.lng } : null);

  // Build standardized activity object
  const activity = {
    id: frontendId,                          // Unique ID for React keys
    title: item.name || item.title,          // Collections use 'title', some APIs use 'name'
    source: source,                          // Track where this came from (for UX)
    savedItemId: item.id,                    // Original clean ID from collections
    placeId: cleanPlaceId,                   // Clean ID for backend (ChIJ..., tm_..., etc.)
    category: item.category || 'activity',
    address: item.location || item.address || item.formatted_address,
    coordinates: coordinates,
    image: item.image_url || item.cover_photo,
    rating: item.rating,
    // Partner catalogs (Viator/Tiqets/tourism) publish a real length under
    // their own field names — carry it through instead of dropping to the
    // server's flat 60-minute default. `item.duration` still wins when the
    // caller set one explicitly.
    duration: item.duration || knownActivityDurationMinutes(item) || null,
    // Preserved so downstream surfaces can show/parse the partner's own
    // phrasing ("2-3 hours", "3 days") without re-deriving it.
    durationText: item.durationText || item.duration_text || null
  };

  // Preserve walking tour fields
  if (item.tourStops) activity.tourStops = item.tourStops;
  if (item.tourId) activity.tourId = item.tourId;
  if (item.icon) activity.icon = item.icon;

  // Preserve tourism experience fields
  if (item.experienceId) activity.experienceId = item.experienceId;
  if (item.transportMode) activity.transportMode = item.transportMode;
  // Where a multi-stop tour starts. Ephemeral, map-fallback only — kept off
  // `coordinates` so it is never persisted as the activity's own position.
  if (item.anchorCoordinates) activity.anchorCoordinates = item.anchorCoordinates;

  // Preserve Viator experience fields
  if (item.viatorProductCode || item.productCode) {
    activity.viatorProductCode = item.viatorProductCode || item.productCode;
    activity.activityType = 'viator_experience';
  }
  if (item.affiliateUrl) activity.affiliateUrl = item.affiliateUrl;

  // A curated walk (docs/WALKS_RUNBOOK.md) is no place: its id is the walk's, and its line rides along so the day's
  // map can draw it before the next trip read. Built by walkActivity, or straight from a trip collection's 'walk' item
  // (the map panel's add, the Add sheet), which carries walk_path / walk_length_m.
  if (item.walkId || item.content_type === 'walk') {
    activity.walkId = item.walkId || item.id;
    activity.activityType = 'walk';
    activity.category = 'walk';
    activity.placeId = undefined;
    const path = item.walkPath || item.walk_path;
    const lengthM = item.lengthM || item.walk_length_m;
    if (path) activity.walkPath = path;
    if (lengthM) activity.lengthM = lengthM;
    if (!item.duration) activity.duration = walkPlannedMinutes(lengthM);
    // Its places, which the add brings onto the day inside it (utils/mapTours walkActivity).
    if (item.walkPlaceItems) activity.walkPlaceItems = item.walkPlaceItems;
    // A saved walk: the planner brings its stops and ends (utils/mapTours walkStopFields).
    if (item.bringWalkPlaces) activity.bringWalkPlaces = true;
  }

  return activity;
};

/**
 * Clone an already-scheduled trip activity into a fresh, unsaved one that can
 * be added to another day ("+ Add to another day" on the map info card).
 *
 * Deliberately NOT createActivityFromItem: that takes a *saved/collection*
 * item, whose `id` IS the clean content id (ChIJ…/tm_…/custom_…). A trip
 * activity's `id` is its row identity instead — the server's activity_id, or
 * this session's `saved_<place>_<ts>` client key — so routing one through
 * there wrote that row id into `placeId`. The save then stored it as the
 * place_id of a place that does not exist and dropped the title (custom_title
 * is only kept for non-Google activities), and the copy came back untitled.
 *
 * Row identity and the old slot are dropped so the save INSERTs a second row
 * rather than matching and MOVING the original; everything describing *what*
 * the activity is (place/event/tour ids, title, coordinates, duration, photo)
 * rides along. Per-visit state — status, notes, highlight/review flags, and
 * the user's own `bookingUrl` reservation link — belongs to the original
 * booking, not to the copy. (Viator/Tiqets get their booking link back
 * server-side, re-derived from the `affiliateUrl` that does ride along.)
 *
 * CAVEAT for tours and partner products: the server's content key for
 * walking_tour / tourism_experience / viator_experience / tiqets_experience
 * carries no day or time (`_buildContentKeyFromIncoming`), so copying one to
 * an EARLIER day lets the copy claim the original's row and move it, while
 * the original inserts fresh — the tagged photos, child stops and any review
 * follow the moved row to the wrong day. Places and events are unaffected
 * (their keys embed trip_day_id). Tracked in docs/FUTURE_WORK.md.
 *
 * @param {Object} activity - Activity as it lives in `tripDays[].activities`
 * @returns {Object|null} Unsaved activity ready for AddToTripModal
 */
export const duplicateActivityForDay = (activity) => {
  if (!activity) return null;

  const {
    // Row identity — carrying any of these makes the save update the original
    id, activityId, activity_id, linkId, link_id,
    // The slot it currently occupies; the target day supplies the new one
    tripDayId, trip_day_id, scheduledDate, scheduled_date,
    scheduledTime, scheduled_time, timeBlock, time_block,
    endDate, end_date, displayOrder, sortOrder,
    // Per-visit state, not part of "this place, again". bookingUrl is the
    // user's own reservation link for THAT sitting (ItineraryTab's booking
    // field) — copying it would point the new day at the old reservation.
    status, notes, isHighlight, excludeFromReview, reviewDismissed, existingReview,
    bookingUrl, booking_url,
    // The star YOU gave that sitting. A copy is a fresh, unrated visit.
    userRating,
    // Map projection bookkeeping (MapTab.expandActivityForMap)
    dayIndex, activityIndex, stopOrder, totalStops,
    // The walk it sat in, and the map's / agenda's walk bookkeeping
    // (utils/dayWalks): a copy on another day is in no walk. Nor was it made
    // by "I'm here" there.
    inWalk, in_walk_id, inWalkRow, walkPlaceCount, mapGroup, isTourStart, tourTitle, tourTimeLabel,
    addedVia, added_via,
    ...content
  } = activity;

  // Mirrors createActivityFromItem's key shape; only ever a React key.
  const contentId = content.placeId || content.eventId || content.tourId
    || content.experienceId || content.viatorProductCode
    || content.tiqetsProductCode || content.walkId || 'activity';

  return { ...content, id: `saved_${contentId}_${Date.now()}` };
};

/**
 * Validates that an activity has required fields for saving
 *
 * @param {Object} activity - Activity object to validate
 * @returns {Object} { isValid: boolean, missingFields: string[] }
 */
export const validateActivity = (activity) => {
  const requiredFields = ['title', 'placeId'];
  const missingFields = requiredFields.filter(field => !activity[field]);

  return {
    isValid: missingFields.length === 0,
    missingFields
  };
};
